<template>
  <div>
    <div v-if="error" class="favourites-not-found">
      <h6>{{ error }}</h6>
    </div>

    <div class="container--plp">
      <div class="oap-card-list oap-card-list--layout-grid">
        <div
          v-for="(product, index) in products"
          :key="product.id || index"
          class="wishlist-card-wrapper"
        >
          <OapCard :item="product" />
          <button class="unlikeBtn" @click="removeProduct(product.id)">
            <img :src="deleteIcon" :alt="deleteAlt" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import eventBus from '@refapp/eventbus-js';
import OapCard from '../../../../../../OapCard/code/Scripts/components/OapCard.vue';

export default {
  name: 'WishlistProduct',
  components: { OapCard },
  props: {
    deleteIcon: {
      type: String,
      default: null,
    },
    deleteAlt: {
      type: String,
      default: null,
    },
    errorMessage: {
      type: String,
      default: 'Failed to load Products',
    },
    notFound: {
      type: String,
      default: 'No Wishlisted Products Found',
    },
  },
  data() {
    return {
      products: [],
      loading: true,
      error: null,
    };
  },

  async mounted() {
    window.addEventListener('storage', this.onStorageChange);
    await this.loadProducts();
  },
  beforeUnmount() {
    window.removeEventListener('storage', this.onStorageChange);
  },
  methods: {
    onStorageChange(event) {
      if (event.key === 'likedProducts') {
        this.loadProducts();
      }
    },
    async loadProducts() {
      this.loading = true;
      try {
        const favoriteProductIds = this.getFavoriteProductIdsFromSession();

        if (favoriteProductIds.length > 0) {
          const productPromises = favoriteProductIds.map((id) => this.fetchProductDetails(id));
          const responses = await Promise.all(productPromises);

          this.products = responses.filter((res) => res !== null).map((p) => this.mapProduct(p));

          this.error = null;
        } else {
          this.products = [];
          this.error = this.notFound;
        }
      } catch (err) {
        this.error = this.errorMessage;
        this.products = [];
      } finally {
        this.loading = false;
      }
    },

    getFavoriteProductIdsFromSession() {
      return JSON.parse(localStorage.getItem('likedProducts')) || [];
    },

    async fetchProductDetails(id) {
      const baseUrl = `${window.location.protocol}//${window.location.host}`;
      const url = `${baseUrl}/product/details?ean=${id}`;
      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`HTTP error! ${response.status}`);
        return await response.json();
      } catch (err) {
        return null;
      }
    },

    mapProduct(product) {
      const ratingValue = !isNaN(parseFloat(product.Rating)) ? parseFloat(product.Rating) : 0;
      const ratingCount = product.RatingCount ? parseInt(product.RatingCount, 10) : 0;
      const ratingMax = 5;

      const ratingData = {
        bv: true,
        rating: ratingValue,
        ratingMax: ratingMax,
        count: ratingCount,
        srText: 'User Rating',
        textColor: null,
      };
      return {
        id: product.Sku,
        type: 'product',
        label: '',
        brand: product.Brand,
        title: product.Name,
        url: product.Url,
        text: '',
        image: {
          fileName: product.ImagePath,
          alt: product.Name,
          resolutions: [{ width: 400, hash: 'default' }],
          sizes: '200px',
          fallbackSize: '200px',
        },
        rating: ratingData,
        action: {
          text: 'View Product',
          buyNowText: 'Buy Now',
        },
        tag: {
          common: {
            product: {
              id: product.Sku || '',
              name: product.Name || '',
              brand: product.Brand || '',
              category: product.Category || '',
              price: product.Price || '',
            },
            data: {
              currency: product?.currency || '',
            },
          },
          productclick: {
            data: {
              label: `${product.Name?.toLowerCase() || ''}::`,
              list: 'list-result-range',
            },
          },
          productimpression: {
            data: {
              label: 'Wishlist page',
            },
            product: {
              list: 'list-result-range',
            },
          },
        },
      };
    },

    removeProduct(productId) {
      const removedProduct = this.products.find((p) => p.id === productId);
      this.products = this.products.filter((p) => p.id !== productId);
      let favoriteProductIds = this.getFavoriteProductIdsFromSession();
      favoriteProductIds = favoriteProductIds.filter((id) => id !== productId);
      localStorage.setItem('likedProducts', JSON.stringify(favoriteProductIds));
      if (this.products.length === 0) {
        this.error = this.notFound;
      }
      eventBus.emit('wishlist-updated');
      if (removedProduct) {
        this.triggerGtm(removedProduct);
      }
    },
    triggerGtm(product) {
      const productInfo = `${product?.title}::${product?.id}`;
      const productWishlistObj = {
        event: 'uaevent',
        ecommerce: 'undefined',
        eventCategory: 'Wishlist',
        eventAction: 'Product Wishlist',
        eventLabel: productInfo,
        product_info: productInfo,
        event_name: 'remove_from_wishlist',
      };
      window.dataLayer?.push(productWishlistObj);
    },
  },
};
</script>

<style lang="scss" scoped>
.unlikeBtn {
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 5;
  width: 32px;
  height: 32px;
  cursor: pointer;
  padding: 0;

  img {
    width: 30px;
    height: 30px;
  }

  @media (min-width: 768px) {
    left: 32px;
  }
}

.wishlist-card-wrapper {
  position: relative;
}

.favourites-not-found {
  text-align: center;
}
</style>
